<template>
    <div
        class="NewFileListInputFilePreview"
        :class="{ 'NewFileListInputFilePreview--error': hasError }"
    >
        <div class="NewFileListInputFilePreview__icon">
            <EidIcon :icon-src="faFileAlt" />
        </div>
        <div class="NewFileListInputFilePreview__content">
            <div class="NewFileListInputFilePreview__name">
                {{ name }}
            </div>
            <div
                v-if="hasError"
                class="NewFileListInputFilePreview__message"
            >
                {{ submissionFile.error }}
            </div>
        </div>
        <div
            v-if="allowRemove"
            class="NewFileListInputFilePreview__remove"
        >
            <NewButton
                variant="flat"
                color="secondary"
                size="icon-sm"
                @click="$emit('remove', submissionFile.file.name)"
            >
                <EidIcon :icon-src="faXmark" />
            </NewButton>
        </div>
    </div>
</template>

<style lang="scss">
    .NewFileListInputFilePreview {
        display: flex;
        align-items: center;
        padding: 11px;
        border: 1px solid #c9d1de;
        border-radius: 4px;
        background: var(--eidWhite);

        &__icon {
            font-size: 2.15em;
            line-height: 1;
            width: 1.25em;
            text-align: center;
            margin-right: 12px;
            color: #4a5d78;
            flex: 0 0 auto;
        }

        &__content {
            flex: 1 1 auto;
            min-width: 0;
        }

        &__name {
            color: #2d3e50;
            font-size: 0.93em;
            line-height: 1.25;
            overflow-wrap: anywhere;
        }

        &__message {
            margin-top: 4px;
            font-size: 0.78em;
            line-height: 1.3;
            color: #be3232;
            overflow-wrap: anywhere;
        }

        &__remove {
            margin-left: 10px;
            flex: 0 0 auto;
        }

        &--error {
            border-color: #be3232;
        }
    }
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { faFileAlt, faXmark } from '@fortawesome/pro-light-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { midellipsis } from '@evidentid/universal-framework/strings';

    import NewButton from '@/components/new-button/NewButton.vue';

    import { NewFileListInputFile } from './types';

    export default defineComponent({
        name: 'NewFileListInputFilePreview',
        components: {
            NewButton,
            EidIcon,
        },
        props: {
            submissionFile: {
                type: Object as PropType<NewFileListInputFile>,
                required: true,
            },
            allowRemove: {
                type: Boolean as PropType<boolean>,
                default: true,
            },
            maxNameLength: {
                type: Number as PropType<number>,
                default: 50,
            },
        },
        emits: [ 'remove' ],
        data() {
            return {
                faFileAlt,
                faXmark,
            };
        },
        computed: {
            hasError(): boolean {
                return Boolean(this.submissionFile.error);
            },
            extension(): string {
                const originalName = this.submissionFile.file.name || '';
                const match = originalName.match(/\.[^.]+$/);
                return match ? match[0] : '';
            },
            name(): string {
                const originalName = this.submissionFile.file.name || '';
                if (originalName.length <= this.maxNameLength) {
                    return originalName;
                }

                const extension = this.extension;
                const nameWithoutExtension = originalName.substr(0, originalName.length - extension.length);
                return `${midellipsis(nameWithoutExtension, this.maxNameLength - extension.length)}${extension}`;
            },
        },
    });
</script>
